import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router"
import { toast } from "sonner"
import { client } from "@client/client"
import { getErrorMessage } from "@client/lib/errors"
import { SUPPORT_EMAIL } from "@shared/contact"
import { useHasPermission } from "./session-context"
import { ArrowLeftIcon, PrinterIcon, RefreshIcon, SendIcon, CopyIcon } from "./components/icons"
import { DetailPageSkeleton } from "./components/skeleton"

type DonationStatus = "pending" | "completed" | "refunded"
type DonationMethod = "card" | "bank_transfer" | "ussd"
type DonationDesignation = "scholarship" | "womens_program" | "general"

interface Donation {
  id: string
  code: string
  receiptCode: string
  donorName: string
  donorEmail: string | null
  donorId: string | null
  donorCode: string | null
  sponsorId: string | null
  sponsorName: string | null
  sponsorCode: string | null
  childId: string | null
  schoolId: string | null
  amount: number
  processorFee: number
  netAmount: number
  method: DonationMethod
  designation: DonationDesignation
  status: DonationStatus
  channel: string | null
  currency: string
  reference: string | null
  activity: Array<{ event: string; date: string }> | null
  donatedAt: string
  createdAt: string
  updatedAt: string
}

const STATUS_COLORS: Record<DonationStatus, { bg: string; text: string }> = {
  completed: { bg: "bg-green-100", text: "text-green-700" },
  pending: { bg: "bg-orange-100", text: "text-orange-600" },
  refunded: { bg: "bg-red-100", text: "text-red-700" },
}

const METHOD_LABELS: Record<DonationMethod, string> = {
  card: "Card",
  bank_transfer: "Bank transfer",
  ussd: "USSD",
}

const DESIGNATION_LABELS: Record<DonationDesignation, string> = {
  scholarship: "Scholarship",
  womens_program: "Women's program",
  general: "General",
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

function formatDate(dateString: string): string {
  return dateString.split("T")[0] ?? dateString
}

function formatRelativeDate(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return "today"
  if (diffDays === 1) return "yesterday"
  if (diffDays < 7) return "this week"
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
  return formatDate(dateString)
}

function SummaryRow({ label, value, isStatus, status }: { label: string; value: string; isStatus?: boolean; status?: DonationStatus }) {
  return (
    <div className="flex items-center justify-between border-b border-ink/10 py-5 last:border-b-0">
      <span className="text-base font-semibold text-ink/50">{label}</span>
      {isStatus && status ? (
        <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${STATUS_COLORS[status].bg} ${STATUS_COLORS[status].text}`}>
          {status}
        </span>
      ) : (
        <span className="text-base font-semibold text-ink text-right">{value}</span>
      )}
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-[10px] border border-gold-500 bg-white p-6">
      <h3 className="mb-4 text-lg font-bold text-forest-950 underline decoration-dotted">{title}</h3>
      <div>{children}</div>
    </div>
  )
}

export function GivingDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const canView = useHasPermission("giving")
  const canMutate = useHasPermission("giving_mutate")
  const [donation, setDonation] = useState<Donation | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState<string | null>(null)

  const fetchDonation = async () => {
    if (!id) return
    try {
      const res = await client.api.admin.giving[":id"].$get({ param: { id } })
      const body = await res.json()
      if (!res.ok) {
        setError(getErrorMessage(body))
        return
      }
      if ("data" in body) {
        setDonation(body.data as Donation)
      }
    } catch {
      setError("Failed to load donation")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (canView) {
      void fetchDonation()
    } else {
      setLoading(false)
    }
  }, [id, canView])

  const handleRefund = async () => {
    if (!id || !donation) return
    if (donation.status === "refunded") {
      toast.error("This donation has already been refunded")
      return
    }
    setActionLoading("refund")
    try {
      const res = await client.api.admin.giving[":id"].refund.$post({ param: { id } })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success("Donation refunded successfully")
      void fetchDonation()
    } catch {
      toast.error("Failed to refund donation")
    } finally {
      setActionLoading(null)
    }
  }

  const handleSendReceipt = async () => {
    if (!id) return
    setActionLoading("receipt")
    try {
      const res = await client.api.admin.giving[":id"]["send-receipt"].$post({ param: { id } })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success("Receipt sent successfully")
      void fetchDonation()
    } catch {
      toast.error("Failed to send receipt")
    } finally {
      setActionLoading(null)
    }
  }

  const copyReference = () => {
    if (donation?.reference) {
      navigator.clipboard.writeText(donation.reference)
      toast.success("Reference copied to clipboard")
    }
  }

  if (!canView) {
    return (
      <div className="space-y-4">
        <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl">Donation Details</h1>
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">You don't have permission to view giving.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return <DetailPageSkeleton />
  }

  if (error || !donation) {
    return (
      <div className="space-y-4">
        <div className="rounded-[10px] border border-red-200 bg-red-50 p-8 text-center">
          <p className="text-red-600">{error ?? "Donation not found"}</p>
        </div>
      </div>
    )
  }

  const feePercent = donation.amount > 0 ? ((donation.processorFee / donation.amount) * 100).toFixed(1) : "0"

  return (
    <>
    <div className="space-y-6 print:hidden">
      {/* Back button */}
      <button
        onClick={() => navigate("/admin/giving")}
        className="flex items-center gap-2 text-ink/60 hover:text-ink"
      >
        <ArrowLeftIcon className="h-5 w-5" />
        <span className="font-semibold">Back</span>
      </button>

      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-2">
          <p className="text-base text-ink/50">{donation.receiptCode}</p>
          <h1 className="font-display text-2xl font-bold text-ink sm:text-4xl">
            Transaction {donation.code}
          </h1>
          <p className="text-base text-ink">{formatDate(donation.donatedAt)}</p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => navigate("/admin/giving")}
            className="inline-flex items-center justify-center rounded-full border border-gold-500 px-4 py-2 text-sm font-medium text-ink hover:bg-cream-50"
          >
            Close
          </button>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-gold-500 px-4 py-2 text-sm font-medium text-ink hover:bg-cream-50"
          >
            <PrinterIcon className="h-4 w-4" />
            Print receipt
          </button>
          {canMutate && donation.status !== "refunded" && (
            <button
              onClick={handleRefund}
              disabled={actionLoading === "refund"}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-red-500 px-4 py-2 text-sm font-medium text-red-500 hover:bg-red-50 disabled:opacity-50"
            >
              <RefreshIcon className="h-4 w-4" />
              {actionLoading === "refund" ? "Refunding..." : "Refund"}
            </button>
          )}
          {canMutate && (
            <button
              onClick={handleSendReceipt}
              disabled={actionLoading === "receipt"}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-forest-950 px-5 py-2 text-sm font-medium text-white hover:bg-forest-900 disabled:opacity-50"
            >
              <SendIcon className="h-4 w-4" />
              {actionLoading === "receipt" ? "Sending..." : "Send receipt"}
            </button>
          )}
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid gap-6">
        {/* Summary Section */}
        <Section title="SUMMARY">
          <SummaryRow label="Transaction ID" value={donation.code} />
          <SummaryRow label="Receipt" value={donation.receiptCode} />
          <SummaryRow label="Date" value={formatDate(donation.donatedAt)} />
          <SummaryRow label="Status" value="" isStatus status={donation.status} />
          <SummaryRow label="Designation" value={DESIGNATION_LABELS[donation.designation]} />
          <SummaryRow label="Method" value={METHOD_LABELS[donation.method]} />
          <SummaryRow label="Sponsor" value={donation.sponsorName ? `${donation.sponsorName} · ${donation.sponsorCode}` : "Not linked"} />
          <SummaryRow label="Child ID" value={donation.childId ?? "Not linked"} />
          <SummaryRow label="School ID" value={donation.schoolId ?? "Not linked"} />
        </Section>

        {/* Donor Section */}
        <Section title="DONOR">
          <SummaryRow label="Name" value={donation.donorName} />
          <SummaryRow label="Email" value={donation.donorEmail ?? "—"} />
          <SummaryRow label="Donor record" value={donation.donorCode ?? "Legacy donation"} />
          <div className="flex items-center justify-between border-b border-ink/10 py-5">
            <span className="text-base font-semibold text-ink/50">Reference</span>
            {donation.reference ? (
              <button
                onClick={copyReference}
                className="flex items-center gap-2 text-base font-semibold text-teal-600 hover:underline"
              >
                Copy ID
                <CopyIcon className="h-5 w-5" />
              </button>
            ) : (
              <span className="text-base font-semibold text-ink/50">—</span>
            )}
          </div>
          <SummaryRow label="Channel" value={donation.channel ?? "—"} />
          <SummaryRow label="Currency" value={donation.currency} />
        </Section>

        {/* Amount Section */}
        <Section title="AMOUNT">
          <SummaryRow label="Gross amount" value={formatCurrency(donation.amount)} />
          <SummaryRow label={`Processor fee (${feePercent}%)`} value={`− ${formatCurrency(donation.processorFee)}`} />
          <SummaryRow label="Net to foundation" value={formatCurrency(donation.netAmount)} />
        </Section>

        {/* Activity Section */}
        {donation.activity && donation.activity.length > 0 && (
          <Section title="ACTIVITY">
            <div className="space-y-4">
              {donation.activity.map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="h-2.5 w-2.5 rounded-full bg-forest-950" />
                  <p className="text-base font-semibold text-ink">
                    {item.event}{" "}
                    <span className="text-ink/50">· {formatRelativeDate(item.date)}</span>
                  </p>
                </div>
              ))}
            </div>
          </Section>
        )}
      </div>
    </div>
    <div className="hidden print:block print:p-10">
      <div className="mx-auto max-w-2xl border border-gray-300 p-10 text-gray-900">
        <div className="border-b border-gray-300 pb-6">
          <p className="text-2xl font-bold">Redempta</p>
          <p className="mt-1 text-xs uppercase tracking-[0.2em] text-gray-500">Development Foundation</p>
        </div>
        <div className="py-8">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500">Donation receipt</p>
          <h1 className="mt-2 text-3xl font-bold">Thank you, {donation.donorName}</h1>
          <p className="mt-2 text-sm text-gray-600">Your donation has been recorded.</p>
        </div>
        <div className="space-y-3 border-y border-gray-300 py-6 text-sm">
          <div className="flex justify-between gap-8"><span className="text-gray-500">Receipt</span><strong>{donation.receiptCode}</strong></div>
          <div className="flex justify-between gap-8"><span className="text-gray-500">Transaction</span><strong>{donation.code}</strong></div>
          <div className="flex justify-between gap-8"><span className="text-gray-500">Date</span><strong>{formatDate(donation.donatedAt)}</strong></div>
          <div className="flex justify-between gap-8"><span className="text-gray-500">Designation</span><strong>{DESIGNATION_LABELS[donation.designation]}</strong></div>
          <div className="flex justify-between gap-8"><span className="text-gray-500">Payment method</span><strong>{METHOD_LABELS[donation.method]}</strong></div>
        </div>
        <div className="space-y-3 py-6 text-sm">
          <div className="flex justify-between gap-8"><span className="text-gray-500">Gross amount</span><strong>{formatCurrency(donation.amount)}</strong></div>
          <div className="flex justify-between gap-8"><span className="text-gray-500">Processing fee</span><strong>{formatCurrency(donation.processorFee)}</strong></div>
          <div className="flex justify-between gap-8 border-t border-gray-300 pt-3 text-base"><span>Net to foundation</span><strong>{formatCurrency(donation.netAmount)}</strong></div>
        </div>
        <p className="border-t border-gray-300 pt-6 text-xs text-gray-500">Questions? Contact {SUPPORT_EMAIL}.</p>
      </div>
    </div>
    </>
  )
}
